import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FaExclamationTriangle } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';

import useMockableSubscription from 'services/apollo/hooks/useMockableSubscription';
import {
  getCurrentHospital,
  subscribeCurrentHospital,
} from 'utils/currentHospital';

import { dismissAlertBar, setAlertBar } from './actions';
import { HOSPITAL_SYNCHRONIZATION_STATUS } from './queries';
import {
  ALERT_BAR_HEIGHT,
  ALERT_BAR_OFFSET_VAR,
  Bar,
  CloseButton,
  Content,
  Message,
  Spacer,
  WarningIcon,
} from './styles';

export default function AlertBar() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { active, timestamp, dismissed } = useSelector((state) => state.alertBar);
  const [hospitalId, setHospitalId] = useState(getCurrentHospital);
  const previousHospitalIdRef = useRef(hospitalId);

  const isVisible = Boolean(active) && !dismissed;

  useEffect(() => subscribeCurrentHospital(setHospitalId), []);

  useEffect(() => {
    if (previousHospitalIdRef.current === hospitalId) return;
    previousHospitalIdRef.current = hospitalId;
    dispatch(setAlertBar({ active: false, timestamp: null }));
  }, [hospitalId, dispatch]);

  useMockableSubscription(HOSPITAL_SYNCHRONIZATION_STATUS, {
    skip: !hospitalId,
    variables: { ids: hospitalId ? [hospitalId] : [] },
    onData: (event) => {
      const payload = event?.data?.data || event?.data;
      const status = payload?.hospitalSynchronizationStatus;
      const item = Array.isArray(status) ? status[0] : status;

      if (!item) return;

      if (item.generationBlocked) {
        dispatch(setAlertBar({
          active: true,
          timestamp: item.blockedAt ?? null,
        }));
        return;
      }

      dispatch(setAlertBar({ active: false, timestamp: null }));
    },
  });

  useEffect(() => {
    const offset = isVisible ? `${ALERT_BAR_HEIGHT}px` : '0px';
    document.documentElement.style.setProperty(ALERT_BAR_OFFSET_VAR, offset);

    return () => {
      document.documentElement.style.setProperty(ALERT_BAR_OFFSET_VAR, '0px');
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const parsed = timestamp ? dayjs(timestamp) : null;
  const date = parsed?.isValid() ? parsed.format('DD/MM') : '--/--';
  const hours = parsed?.isValid() ? parsed.format('HH:mm') : '--:--';

  return (
    <>
      <Spacer aria-hidden />
      <Bar role="alert">
        <Content>
          <WarningIcon aria-hidden>
            <FaExclamationTriangle />
          </WarningIcon>
          <Message>
            {t('text.pepSystemBlocked', { date, hours })}
          </Message>
        </Content>
        <CloseButton
          type="button"
          aria-label={t('button.close')}
          onClick={() => dispatch(dismissAlertBar())}
        >
          <IoMdClose />
        </CloseButton>
      </Bar>
    </>
  );
}
